// Page-specific data for Aravi Eco Homes — A Nature Crafted Community
// All amenities, team, milestones, principles, certifications, connectivity data live here.

// "Landscapes" page is reframed as THE SETTING — surroundings of the project
export const SETTING_FEATURES = [
  {
    slug: 'observatory-belt',
    name: 'Observatory Belt',
    badge: '1,100 acres',
    image: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80',
    blurb: 'The community is wrapped by the Osmania Geological Observatory (forest) — 1,100 acres of protected, undisturbed forest. A neighbour that will not change.',
  },
  {
    slug: 'bodakunta-waterfall',
    name: 'Bodakunta Waterfall',
    badge: '~10 min',
    image: 'https://images.unsplash.com/photo-1432405972618-c60b0225b8f9?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80',
    blurb: 'A short walk-and-drive brings you to a natural waterfall — quiet on weekdays, full of birdsong on weekends.',
  },
  {
    slug: 'shivannaguda',
    name: 'Shivannaguda Reservoir',
    badge: '~25 min',
    image: '/landscapes/shivannaguda-reservoir.jpg',
    blurb: 'A working reservoir within an easy drive — expansive water, hill silhouettes, and a quiet weekend ride along country roads.',
  },
  {
    slug: 'hill-view',
    name: 'Total Hill View',
    badge: 'Every plot',
    image: 'https://images.unsplash.com/photo-1508739773434-c26b3d09e071?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80',
    blurb: 'The site reads the land as a slow bowl. Every plot opens to the surrounding ridge line — not to the next house.',
  },
  {
    slug: 'araku',
    name: 'Telangana Araku Resort',
    badge: 'Opposite the Site',
    image: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80',
    blurb: 'The state-run Araku Resort sits directly opposite the community — a hospitality anchor at the doorstep.',
  },
];

// Connectivity & proximity grid — approximate driving times from the gate
export const CONNECTIVITY = [
  { label: 'ORR Exit (Double Road)', distance: '~15 min' },
  { label: 'Telangana Araku Resort', distance: 'Opposite Site' },
  { label: 'Bodakunta Waterfall', distance: '~10 min' },
  { label: 'Shivannaguda Reservoir', distance: '~25 min' },
  { label: 'BDL · Guru Nanak University', distance: '~15 min' },
  { label: 'Gurukul Vidyapeeth School', distance: '~15 min' },
  { label: 'Scient Engineering College', distance: '~15 min' },
  { label: 'CVR · Bharath · Siddartha Colleges', distance: '~20 min' },
  { label: 'Manchester Global School', distance: '~30 min' },
  { label: 'Adibatla IT Hub', distance: '~35 min' },
  { label: 'Ranga Reddy Collectorate', distance: '~35 min' },
  { label: 'Rajiv Gandhi Int. Airport', distance: '~50 min' },
];

// Four principles — the "Nature Crafted" ethos
export const PHILOSOPHY_PRINCIPLES = [
  {
    number: '01',
    title: 'Discovered. Then Planned.',
    body: 'We did not buy land and impose a layout. We walked this land, listened to it, and let the masterplan answer to its slopes, its hill view, and the 1,100-acre observatory belt it inherits as a neighbour.',
  },
  {
    number: '02',
    title: 'Legally Clear, Transparently Built',
    body: 'Every plot falls within HMDA Limits. Every customer sees the same published rate, the same documentation, and the same monthly work-progress update. No surprises is a design principle here.',
  },
  {
    number: '03',
    title: 'A Community, Not a Compound',
    body: 'Miyawaki forest, koneru, natural farm, amphitheatre — 25 acres of shared land, designed to host 110 households the way an old village square once did.',
  },
  {
    number: '04',
    title: 'Value Before Volume',
    body: 'We are priced at roughly half of comparable gated communities in the corridor. Not because we have less to offer — because we have already proved we don\'t need to inflate the land to fund the lifestyle.',
  },
];

// Leadership team — grouped
export const TEAM_GROUPS = [
  {
    title: 'Founding Leadership',
    members: [
      { name: 'Ravikumar Jatoth', role: 'Founder & Chairman', initials: 'RJ' },
      { name: 'Dr. Ramavath Annapurna', role: 'Co-Founder & Director', initials: 'RA' },
    ],
  },
  {
    title: 'Executive Leadership',
    members: [
      { name: 'CA Saidulu Diddi', role: 'Chief Financial Officer', initials: 'SD' },
      { name: 'Sreenivas', role: 'Vice President — Marketing & Sales', initials: 'SR' },
      { name: 'Mr. Chandrakanth', role: 'Head — Construction & Projects', initials: 'CK' },
      { name: 'Dr. V. Baburao', role: 'Head — Ecology, Landscaping & Sustainability', initials: 'BR' },
    ],
  },
  {
    title: 'Design Partner',
    members: [
      { name: 'Vishwa Architects', role: 'Principal Architect', initials: 'VA' },
    ],
  },
];

export const MILESTONES = [
  { year: 'Origin', text: 'Ravikumar Jatoth and Dr. Ramavath Annapurna found Aravi Eco Homes Pvt. Ltd. with a simple brief — find the land first, build only what it will hold.' },
  { year: 'Site', text: 'The 25-acre Ibrahimpatnam site is selected — adjacent to the 1,100-acre Osmania Geological Observatory belt (forest), with a total hill view on every horizon.' },
  { year: 'Approvals', text: 'The community is brought within HMDA Limits. Plot titles, layouts and the published rate of ₹10,999 per sq yard are publicly documented.' },
  { year: 'Phase I', text: 'The 605 and 1210 home configurations open for booking across 110 villas. The Miyawaki forest belt and the natural farm break ground.' },
  { year: 'Today', text: 'Aravi welcomes families across the Hyderabad corridor — half the price of comparable gated projects, twice the green.' },
];

export const CERTIFICATIONS = [
  { name: 'HMDA Limits Layout', mark: 'HMDA' },
  { name: 'CREDAI Hyderabad Member', mark: 'CREDAI', logo: '/certifications/credai-hyderabad.jpg', logoBg: 'light' },
  { name: 'IGBC Green Community Member', mark: 'IGBC', logo: '/certifications/igbc.png', logoBg: 'dark' },
  { name: 'Published ₹10,999 per Sq Yard', mark: '10999', showRupee: true },
];

// Amenities — the long real list, organised into readable categories
export const AMENITY_GROUPS = [
  {
    title: 'The Living Land',
    items: ['Miyawaki Forest Belt', 'Natural Farm', 'Farm Store', 'Koneru (Stepped Pond)', 'Water Stream', 'Rain Garden', 'Retention Pond'],
  },
  {
    title: 'Wellness & Recreation',
    items: ['Wellness Lounge', 'Yoga Deck', 'Biopool', 'Amphitheatre', 'Gym', 'Indoor & Outdoor Games', 'Golf · Badminton · Volleyball', 'Cricket Net', 'Sports Arena', 'Trekking Trails', 'Cycling Track', 'Walking Track', 'Pet Park'],
  },
  {
    title: 'Community & Hospitality',
    items: ['Clubhouse', 'Restaurant', 'Co-working Space', 'Banquet Hall', 'Home Theatre', 'Guest Rooms', 'Lawns', 'Senior Citizen Seating', 'Rachabanda · Arugu Sitouts', 'Gazebo'],
  },
  {
    title: 'Home Comforts',
    items: ['Customised G+1 Homes', 'Verandah · Sitout', 'Balcony', 'Terrace', 'Parking · Visitor Parking', 'Health Lounge'],
  },
  {
    title: 'Infrastructure',
    items: ['Solar Power', 'Rainwater Harvesting', 'STP (Sewage Treatment)', 'EV Charging Station', 'Security at Entrance', 'Boom Barriers · CCTV', 'Fresh Air · 75% Oxygen', 'Underground Utilities'],
  },
];
